import { useState } from "react";
import { useIntl } from "react-intl";
import { useMutation } from "@apollo/client/react";
import { useMe } from "@/auth/use-me";
import {
  CreatePinGroupDocument,
  UpdatePinGroupDocument,
  type PinType,
} from "@/graphql/generated/operations";
import { SaveGroupNameDialog } from "./SaveCollectionTemplate";
import { logApplicationFailure } from "@/lib/user-error";

// Create or rename a pin group. Same dialog, mode-driven: "create" needs the
// pinType (groups are typed by PinType), "rename" carries the existing group.
// Validation issues from genesis (ValidatePinGroupResult) surface inline.

type Mode = "create" | "rename";
type Status = "idle" | "saving" | "error";

interface PinGroupDialogProps {
  open: boolean;
  onClose: () => void;
  mode: Mode;
  pinType: PinType;
  group?: { id: string; name: string };
  onSaved: () => void;
}

export function PinGroupDialog({ open, onClose, mode, pinType, group, onSaved }: PinGroupDialogProps) {
  const me = useMe();
  const intl = useIntl();
  const [status, setStatus] = useState<Status>("idle");
  const [issue, setIssue] = useState<string | null>(null);

  // Dialog stays mounted across open/close; reset transient status/issue on each
  // (re)open so a stuck "saving" doesn't disable submit (mirrors the seed below).
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) {
      setStatus("idle");
      setIssue(null);
    }
  }

  const [createGroup] = useMutation(CreatePinGroupDocument);
  const [updateGroup] = useMutation(UpdatePinGroupDocument);

  // A blank server issue message renders as nothing (the dialog hides a falsy issue), which
  // would leave the group uncreated with no reason shown — so treat empty/whitespace as
  // absent, not just null/undefined.
  const invalidNameIssue = (message?: string): string =>
    message?.trim() ||
    intl.formatMessage({ id: "save.pinGroupDialog.invalidName", defaultMessage: "Invalid name." });

  async function save(trimmed: string) {
    if (!trimmed || status === "saving") return;
    setStatus("saving");
    setIssue(null);
    try {
      if (mode === "create") {
        const { data } = await createGroup({
          variables: {
            input: { person: me.id ?? undefined, data: { name: trimmed, pinType }, dryRun: false },
          },
        });
        const res = data?.createPinGroup;
        if (res?.__typename === "ValidatePinGroupResult") {
          setIssue(invalidNameIssue(res.issues[0]?.message));
          setStatus("error");
          return;
        }
        if (res?.__typename === "TooManyRecordsResult") {
          setIssue(
            intl.formatMessage(
              {
                id: "save.pinGroupDialog.limitReached",
                defaultMessage: "You have reached the maximum of {limit} groups.",
              },
              { limit: res.limit ?? "" },
            ),
          );
          setStatus("error");
          return;
        }
        if (res?.__typename !== "CreatePinGroupResult") {
          setIssue(intl.formatMessage({ id: "save.pinGroupDialog.createFailed", defaultMessage: "The group could not be created." }));
          setStatus("error");
          return;
        }
      } else {
        if (!group) return;
        const { data } = await updateGroup({
          variables: { id: group.id, input: { data: { name: trimmed } } },
        });
        const res = data?.updatePinGroup;
        if (res?.__typename === "ValidatePinGroupResult") {
          setIssue(invalidNameIssue(res.issues[0]?.message));
          setStatus("error");
          return;
        }
        if (res?.__typename !== "UpdatePinGroupResult") {
          setIssue(intl.formatMessage({ id: "save.pinGroupDialog.renameFailed", defaultMessage: "The group could not be renamed." }));
          setStatus("error");
          return;
        }
      }
      onSaved();
      onClose();
    } catch {
      logApplicationFailure("save.pin-group");
      setIssue(intl.formatMessage({ id: "save.pinGroupDialog.tryAgain", defaultMessage: "Please try again." }));
      setStatus("error");
    }
  }

  return (
    <SaveGroupNameDialog
      key={`${mode}-${group?.id ?? pinType}`}
      open={open}
      onClose={onClose}
      title={
        mode === "create"
          ? intl.formatMessage({ id: "save.pinGroupDialog.titleCreate", defaultMessage: "New group" })
          : intl.formatMessage({ id: "save.pinGroupDialog.titleRename", defaultMessage: "Rename group" })
      }
      initial={group?.name ?? ""}
      placeholder={intl.formatMessage({ id: "save.pinGroupDialog.placeholder", defaultMessage: "e.g. Mentors" })}
      saving={status === "saving"}
      issue={issue}
      submitLabel={
        mode === "create"
          ? intl.formatMessage({ id: "save.pinGroupDialog.submitCreate", defaultMessage: "Create" })
          : intl.formatMessage({ id: "common.save", defaultMessage: "Save" })
      }
      onSave={(name) => void save(name)}
    />
  );
}
